import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
    reduxForm,
    Field,
    getFormValues,
    change as reduxChange,
} from 'redux-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '../../control/button/Button';
import DatePicker from '../../control/fdatetime/DateflatPicker';
import AutoComplete from '../../control/autocomplete/AutoComplete';
import AutoCompleteFreeInput from '../../control/autocomplete/AutoCompleteFreeInput';
import TextFields from '../../control/textfield/TextFields';
import {
    optionIsPublic,
    optionIsUnlimitedCapacity,
    optionStatus,
} from './FormUtils';
import AutoCompleteMultiSelect from '../../control/autocomplete/AutoCompleteMultiSelect';
import UseAlertMessageState from '../../control/alert/UseAlertMessageState';
import * as AlertMSGConstants from '../../constants/AlertMSGConstants';
import * as ApiEvent from '../../api/ApiEvent';
import { debounce } from 'lodash';
import AlertMessage from '../../control/alert/AlertMessage';

const useStyles = makeStyles((theme) => ({
    spacing: {
        ...theme.form.spacing,
    },
    btnSearch: {
        width: '105px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    btnReset: {
        backgroundColor: `${theme.mainTheme.color6} !important`,
        width: '105px',
        marginRight: '8px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginRight: '0px',
            marginBottom: '2%',
        },
    },
    dateTimeContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        alignItems: 'center',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            width: '100%',
            alignItems: 'flex-start',
            gap: '4px',
        },
    },
    dateTimeContainerField: {
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flex: 1,
        },
    },
}));

const formId = 'EventSearchForm';

const SearchForm = ({
    initialize,
    reset,
    change,
    defaultValues,
    lookups,
    showOrganization,
    onSubmit,
}) => {
    const classes = useStyles();
    const store = useStore();
    const values = useSelector((state) => getFormValues(formId)(state));
    const dispatch = useDispatch();

    const [selectItem, setSelectItem] = useState([]);

    const alertMessageState = UseAlertMessageState();

    const [isEventOptionLoading, setIsEventOptionLoading] = useState(false);
    const [isUsersOptionLoading, setIsUsersOptionLoading] = useState(false);

    const [selectOptionEvent, setSelectOptionEvent] = useState([]);
    const [selectOptionUsers, setSelectOptionUsers] = useState([]);

    const [tempEventName, setTempEventName] = useState('');
    const [tempCoordinators, setTempCoordinators] = useState('');

    const handleSelect = (listSelectedID = [], listSelcectedArr) => {
        change('statusIds', listSelcectedArr);
        setSelectItem(listSelectedID);
    };

    useEffect(() => {
        initialize({ ...defaultValues });
    }, []);

    const handleChangeDate = useCallback(
        (field) => (value) => {
            if (value) {
                switch (field) {
                    case 'eventStartDate':
                        if (
                            values.eventEndDate &&
                            value > values.eventEndDate
                        ) {
                            change('eventEndDate', value);
                        }
                        break;
                    case 'eventEndDate':
                        if (
                            values.eventStartDate &&
                            value < values.eventStartDate
                        ) {
                            change('eventStartDate', value);
                        }
                        break;
                    case 'reserveStartDate':
                        if (
                            values.reserveEndDate &&
                            value > values.reserveEndDate
                        ) {
                            change('reserveEndDate', value);
                        }
                        break;
                    case 'reserveEndDate':
                        if (
                            values.reserveStartDate &&
                            value < values.reserveStartDate
                        ) {
                            change('reserveStartDate', value);
                        }
                        break;
                    default:
                        break;
                }
            }
        },
        [values, change]
    );

    const handleClickSearch = useCallback(() => {
        const currentValues = getFormValues(formId)(store.getState());
        const _values = { ...currentValues };
        if (!showOrganization) {
            delete _values.orgId;
        }
        onSubmit(_values);
    }, [showOrganization, onSubmit, store]);

    const handleClickReset = useCallback(() => {
        reset(formId);
        setSelectItem([]);
        handleClickSearch();
    }, [reset, handleClickSearch]);

    const showAlert = useCallback(() => {
        alertMessageState.showAlert({
            altMsg: AlertMSGConstants.SYSTEM_ERROR,
        });
    }, [alertMessageState]);

    const fetchEventNameOptionSearch = useCallback(async (keyword) => {
        try {
            // values.eventName
            setIsEventOptionLoading(true);
            if (keyword) {
                setSelectOptionEvent([]);
                const res = await ApiEvent.getOptionEventSearchName({
                    keyword,
                });
                if (res.data && res.data.length > 0) {
                    setSelectOptionEvent(res.data);
                }
            }
        } catch (err) {
            showAlert();
        } finally {
            setIsEventOptionLoading(false);
        }
    }, []);

    const fetchUsersOptionSearch = useCallback(async (keyword) => {
        try {
            // values.coordinators
            setIsUsersOptionLoading(true);
            if (keyword) {
                setSelectOptionUsers([]);
                const res = await ApiEvent.getOptionUsersSearchEmployee({
                    keyword,
                });
                if (res.data && res.data.length > 0) {
                    const options = res.data.map((user) => {
                        return {
                            fullString: `${user.empId} ${user.firstName} ${user.lastName}`,
                            empId: user.empId,
                        };
                    });
                    setSelectOptionUsers(options);
                }
            }
        } catch (err) {
            showAlert();
        } finally {
            setIsUsersOptionLoading(false);
        }
    }, []);

    const handleChangeTempEventName = useCallback((value) => {
        setTempEventName(value);
    }, []);

    const handleChangeTempUsers = useCallback((value) => {
        setTempCoordinators(value);
    }, []);

    const handleChangeDropDownEventName = useCallback(
        (selectedValue) => {
            if (selectedValue) {
                dispatch(
                    reduxChange(formId, 'eventName', selectedValue.eventName)
                );
            }
        },
        [dispatch]
    );

    const debounceEventOptionSearch = useMemo(
        () => debounce(fetchEventNameOptionSearch, 500),
        []
    );

    const debounceUsersOptionSearch = useMemo(
        () => debounce(fetchUsersOptionSearch, 500),
        []
    );

    useEffect(() => {
        if (tempEventName) {
            debounceEventOptionSearch(tempEventName);
        } else {
            setSelectOptionEvent([]);
        }
    }, [tempEventName, debounceEventOptionSearch]);

    useEffect(() => {
        if (tempCoordinators && tempCoordinators.length >= 3) {
            debounceUsersOptionSearch(tempCoordinators);
        } else {
            setSelectOptionUsers([]);
        }
    }, [tempCoordinators, debounceUsersOptionSearch]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} md={4} lg={4} className={classes.spacing}>
                <Field
                    name="eventName"
                    label="ชื่อกิจกรรม"
                    component={AutoCompleteFreeInput}
                    options={selectOptionEvent}
                    optionKey={{
                        label: 'eventName',
                        value: 'eventName',
                    }}
                    placeholder="ไม่ระบุ"
                    isShowNoSelect={false}
                    isEditValue={true}
                    onChangeInputValue={handleChangeTempEventName}
                    loading={isEventOptionLoading}
                    onChangeDropDown={handleChangeDropDownEventName}
                    maxLength={100}
                />
            </Grid>
            <Grid item xs={12} md={4} lg={4} className={classes.spacing}>
                <Field
                    name="isPublic"
                    label="การเผยแพร่กิจกรรม"
                    component={AutoComplete}
                    options={optionIsPublic}
                    optionKey={{
                        label: 'label',
                        value: 'value',
                    }}
                    placeholder="ทั้งหมด"
                    noSelectOption={'ทั้งหมด'}
                    maxLength={100}
                />
            </Grid>
            <Grid item xs={12} md={4} lg={4} className={classes.spacing}>
                <Field
                    name="coordinators"
                    label="ผู้ประสานงานหลัก/ผู้ช่วยจัดกิจกรรม"
                    component={AutoCompleteFreeInput}
                    options={selectOptionUsers}
                    optionKey={{
                        label: 'fullString',
                        value: 'empId',
                    }}
                    placeholder="ไม่ระบุ"
                    isShowNoSelect={false}
                    isEditValue={true}
                    onChangeInputValue={handleChangeTempUsers}
                    loading={isUsersOptionLoading}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={6} className={classes.spacing}>
                <Field
                    name="isUnlimitedCapacity"
                    label="เงื่อนไขการลงทะเบียน"
                    component={AutoComplete}
                    options={optionIsUnlimitedCapacity}
                    optionKey={{
                        label: 'label',
                        value: 'value',
                    }}
                    placeholder="ทั้งหมด"
                    noSelectOption={'ทั้งหมด'}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={6} className={classes.spacing}>
                <Field
                    name="statusIds"
                    label="สถานะกิจกรรม"
                    placeholder="ทั้งหมด"
                    component={AutoCompleteMultiSelect}
                    optionKey={{
                        value: 'id',
                        label: 'label',
                    }}
                    options={optionStatus}
                    listSelectedCheckbox={selectItem}
                    defaultValue={values && values.statusIds}
                    onClickCheckbox={handleSelect}
                />
            </Grid>

            <Grid
                item
                xs={12}
                md={12}
                lg={6}
                className={classes.dateTimeContainer}
            >
                <Field
                    name="reserveStartDate"
                    label="ช่วงวันที่ลงทะเบียนล่วงหน้า"
                    component={DatePicker}
                    placeholder="วัน/เดือน/ปี"
                    className={classes.dateTimeContainerField}
                    onChange={handleChangeDate('reserveStartDate')}
                />
                <div style={{ padding: '2px' }}>ถึง</div>
                <Field
                    name="reserveEndDate"
                    label={'\u00a0'}
                    component={DatePicker}
                    placeholder="วัน/เดือน/ปี"
                    className={classes.dateTimeContainerField}
                    onChange={handleChangeDate('reserveEndDate')}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={12}
                lg={6}
                className={classes.dateTimeContainer}
            >
                <Field
                    name="eventStartDate"
                    label="ช่วงวันที่จัดกิจกรรม"
                    component={DatePicker}
                    placeholder="วัน/เดือน/ปี"
                    className={classes.dateTimeContainerField}
                    onChange={handleChangeDate('eventStartDate')}
                />
                <div style={{ padding: '2px' }}>ถึง</div>
                <Field
                    name="eventEndDate"
                    label={'\u00a0'}
                    component={DatePicker}
                    placeholder="วัน/เดือน/ปี"
                    className={classes.dateTimeContainerField}
                    onChange={handleChangeDate('eventEndDate')}
                />
            </Grid>

            <Grid
                item
                xs={12}
                lg={12}
                container
                justify="flex-end"
                alignItems="center"
            >
                <Button
                    className={classes.btnReset}
                    onClick={handleClickReset}
                    label="ค่าเริ่มต้น"
                />
                <Button
                    className={classes.btnSearch}
                    onClick={handleClickSearch}
                    label="ค้นหา"
                />
            </Grid>
            <AlertMessage {...alertMessageState.altProps} />
        </Grid>
    );
};

export default reduxForm({
    form: formId,
})(SearchForm);
